import React, {useState, useEffect, useMemo} from "react";
import {apiHelper, baseUrl} from "helpers/util";
import {message} from "antd";

const SHORT_LABELS = {
    "FOLK Graduate": "Grad",
    "FOLK Foundation": "Found",
    "FOLK Prelims": "Pre",
    "FOLK Enhanced": "Enh",
    "On Time": "On",
    Late: "Late",
};

const LEVEL_COLORS = {
    "FOLK Foundation": "#35a4dcff",
    "FOLK Prelims": "#ffc107",
    "FOLK Enhanced": "#20c997",
    "FOLK Graduate": "#fd0d9dff",
    "On Time": "#198754",
    Late: "#ff6b6b",
};

const LEVELS = ["FOLK Foundation", "FOLK Prelims", "FOLK Enhanced", "FOLK Graduate"];

export default function TestOntimeBar({getTodayDate, onClose}) {
    const [data, setData] = useState(null);
    const [bars, setBars] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await apiHelper.get({
                    url: `${baseUrl}/.netlify/functions/ontime_report`,
                });

                if (!res.status || !res.json) {
                    message.error("Failed to load ontime report");
                    return;
                }

                const report = res.json;
                const onTimeSummary = report.summary?.["On Time"] || {};

                const levelBars = LEVELS.map((level) => ({
                    key: level,
                    label: SHORT_LABELS[level],
                    value: onTimeSummary[level] || 0,
                    color: LEVEL_COLORS[level],
                }));

                const finalBars = [
                    ...levelBars,
                    {
                        key: "On Time",
                        label: "On",
                        value: report.ontimeCount || 0,
                        color: LEVEL_COLORS["On Time"],
                    },
                    {
                        key: "Late",
                        label: "Late",
                        value: report.lateCount || 0,
                        color: LEVEL_COLORS["Late"],
                    },
                ];

                setData(report);
                setBars(finalBars);
            } catch (err) {
                console.error(err);
                message.error("Error loading ontime report");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const maxValue = useMemo(() => {
        return Math.max(...bars.map((b) => b.value), 1);
    }, [bars]);

    if (loading || !data || bars.length === 0) return null;

    return (
        <div
            style={{
                position: "relative",
                background: "#f8f9fa",
                padding: 16,
                borderRadius: 12,
                border: "1px solid #dee2e6",
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            {/* CLOSE */}
            <button
                onClick={onClose}
                style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    border: "none",
                    background: "#fff",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    cursor: "pointer",
                }}
            >
                ×
            </button>

            {/* HEADER */}
            <h3>⏰ Ontime vs Late Attendance</h3>
            <div style={{color: "#666", fontSize: 13}}>{getTodayDate()}</div>
            <div style={{color: "#666", fontSize: 12, marginBottom: 12}}>Cutoff: {data.cutoffTime}</div>

            {/* CHART */}
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 10,
                    height: 240, // 🔒 FIXED HEIGHT
                    padding: 12,
                    background: "#fff",
                    borderRadius: 8,
                    border: "1px solid #e9ecef",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {bars.map((bar) => {
                    const height = Math.round(
                        (bar.value / maxValue) * 160 // 🔒 SAFE MAX HEIGHT
                    );

                    return (
                        <div
                            key={bar.key}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minWidth: 48,
                                flexShrink: 0,
                            }}
                        >
                            <div style={{fontSize: 12, fontWeight: 600}}>{bar.value}</div>

                            <div
                                style={{
                                    width: 36,
                                    height: Math.max(height, 8),
                                    backgroundColor: bar.color,
                                    borderRadius: "6px 6px 0 0",
                                }}
                            />

                            <div
                                style={{
                                    marginTop: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: bar.color,
                                }}
                            >
                                {bar.label}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* FOOTER */}
            <div
                style={{
                    marginTop: 12,
                    padding: 10,
                    background: "#d1ecf1",
                    borderRadius: 6,
                    fontWeight: "bold",
                    textAlign: "center",
                }}
            >
                Overall Total: {data.overallCount}
            </div>
        </div>
    );
}
